<template>
  <PublicFormLayout>
    <PublicFormNotice v-if="isLoading" tone="loading" :title="t('common.loading')" />

    <PublicFormNotice v-else-if="loadError" tone="danger" :title="loadError" />

    <PublicFormNotice
      v-else-if="isUploaded"
      tone="success"
      :title="t('candidate.public_upload.success_title')"
      :text="t('candidate.public_upload.success_text')" />

    <PublicFormNotice
      v-else-if="unavailableNotice"
      tone="danger"
      :title="unavailableNotice.title"
      :text="unavailableNotice.text" />

    <template v-else-if="fileRequest">
      <div class="tw:mb-7">
        <h1 class="tw:mt-0! tw:mb-2! tw:text-20! tw:font-bold! tw:text-gray-900">
          {{ t('candidate.public_upload.title') }}
        </h1>
        <p class="tw:mt-0! tw:mb-3! tw:text-14 tw:text-gray-600">{{ introText }}</p>
        <p class="tw:m-0! tw:whitespace-pre-wrap tw:text-14 tw:text-gray-900">{{ requestDescription }}</p>
      </div>

      <form @submit.prevent="submit">
        <label
          class="tw:relative tw:block! tw:cursor-pointer tw:rounded-md tw:border-2 tw:border-dashed tw:px-6 tw:py-8 tw:text-center tw:transition-colors tw:hover:border-primary tw:hover:bg-primary-lt"
          :class="isDragging ? 'tw:border-primary tw:bg-primary-lt' : 'tw:border-gray-300'"
          @drop.prevent="onDrop"
          @dragover.prevent
          @dragenter.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false">
          <input
            type="file"
            class="tw:sr-only"
            multiple
            :accept="FILE_UPLOAD_ALLOWED_MIME_TYPES.join(',')"
            :disabled="isUploading"
            @change="onFileChange" />
          <IconUpload :size="24" class="tw:mb-2 tw:text-gray-500" />
          <span class="tw:block tw:text-14 tw:text-gray-700">{{ t('candidate.public_upload.drop_hint') }}</span>
          <span class="tw:block tw:text-13 tw:text-gray-500">
            {{
              t('candidate.public_upload.constraints', {
                size: FILE_UPLOAD_MAX_MEGABYTES,
                count: FILE_UPLOAD_MAX_COUNT,
              })
            }}
          </span>
        </label>

        <ul v-if="selectedFiles.length" class="tw:mt-3! tw:mb-0! tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0!">
          <li
            v-for="(file, index) in selectedFiles"
            :key="`${file.name}-${file.size}`"
            class="tw:flex tw:items-center tw:gap-2 tw:rounded-sm tw:border tw:border-solid tw:border-gray-200 tw:bg-gray-50 tw:py-1 tw:pr-1 tw:pl-3 tw:text-13">
            <span class="tw:flex-1 tw:truncate tw:font-medium tw:text-gray-900">{{ file.name }}</span>
            <span class="tw:shrink-0 tw:text-12 tw:text-gray-500">{{ formatFileSize(file.size) }}</span>
            <LeadButton
              variant="ghost"
              size="sm"
              :icon="IconX"
              :disabled="isUploading"
              :aria-label="t('candidate.public_upload.remove_file', { name: file.name })"
              @click="removeFile(index)" />
          </li>
        </ul>

        <p v-if="uploadError" class="tw:mt-2! tw:mb-0! tw:text-13 tw:text-danger" role="alert">{{ uploadError }}</p>

        <LeadButton
          type="submit"
          variant="primary"
          block
          class="tw:mt-5!"
          :loading="isUploading"
          :disabled="!selectedFiles.length">
          {{ submitLabel }}
        </LeadButton>
      </form>
    </template>
  </PublicFormLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import { IconUpload, IconX } from '@tabler/icons-vue';
import { ClientError } from '@/api/client';
import { fetchPublicFileRequest, isOrderFinishedError, uploadFileRequestFiles } from '@/api/publicForm';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import PublicFormLayout from '@/components/candidate/public/PublicFormLayout.vue';
import PublicFormNotice from '@/components/candidate/public/PublicFormNotice.vue';
import {
  FILE_UPLOAD_ALLOWED_MIME_TYPES,
  FILE_UPLOAD_MAX_BYTES,
  FILE_UPLOAD_MAX_COUNT,
  fileRequestStatus,
} from '@/helpers/candidateMessaging';
import { formatFileSize } from '@/func/string/fileSize';
import { createPostingTranslator } from '@/locale/translator';
import type { PublicFileRequest } from '@/types/candidate';

const FILE_UPLOAD_MAX_MEGABYTES = FILE_UPLOAD_MAX_BYTES / (1024 * 1024);

/* ---------------------------------- STATE --------------------------------- */
const route = useRoute();
const token = route.params.token as string;

// replaced by the posting's language once the request is loaded
const postingTranslator = shallowRef(createPostingTranslator(null));

const isLoading = ref(true);
const loadError = ref<string | null>(null);
const fileRequest = ref<PublicFileRequest | null>(null);

const isDragging = ref(false);
const selectedFiles = ref<File[]>([]);
const isUploading = ref(false);
const uploadError = ref<string | null>(null);
const isUploaded = ref(false);

/* -------------------------------- COMPUTED -------------------------------- */
const introText = computed(() =>
  fileRequest.value?.company_name
    ? t('candidate.public_upload.intro', { company: fileRequest.value.company_name })
    : t('candidate.public_upload.intro_no_company')
);

const requestDescription = computed(() => {
  switch (fileRequest.value?.macro_key) {
    case 'cv_upload':
      return t('candidate.public_upload.request_cv');
    case 'reference_upload':
      return t('candidate.public_upload.request_reference');
    default:
      return fileRequest.value?.custom_message || t('candidate.public_upload.request_custom');
  }
});

const unavailableNotice = computed(() => {
  if (!fileRequest.value) return null;

  switch (fileRequestStatus(fileRequest.value)) {
    case 'fulfilled':
      return {
        title: t('candidate.public_upload.fulfilled_title'),
        text: t('candidate.public_upload.fulfilled_text'),
      };
    case 'cancelled':
      return { title: t('candidate.public_upload.cancelled_title'), text: undefined };
    case 'expired':
      return { title: t('candidate.public_upload.expired_title'), text: undefined };
    default:
      return null;
  }
});

const submitLabel = computed(() =>
  selectedFiles.value.length > 1
    ? t('candidate.public_upload.submit_count', { count: selectedFiles.value.length })
    : t('candidate.public_upload.submit')
);

/* --------------------------------- METHODS -------------------------------- */
function t(key: string, params?: { [key: string]: string | number }): string {
  return postingTranslator.value.get(key, params);
}

function addFiles(incomingFiles: File[]): void {
  uploadError.value = null;
  const mergedFiles = [...selectedFiles.value];

  for (const file of incomingFiles) {
    if (mergedFiles.some((selectedFile) => selectedFile.name === file.name && selectedFile.size === file.size)) {
      uploadError.value = t('candidate.public_upload.error_duplicate', { name: file.name });
      continue;
    }
    if (mergedFiles.length >= FILE_UPLOAD_MAX_COUNT) {
      uploadError.value = t('candidate.public_upload.error_too_many', { count: FILE_UPLOAD_MAX_COUNT });
      break;
    }
    if (file.size > FILE_UPLOAD_MAX_BYTES) {
      uploadError.value = t('candidate.public_upload.error_too_large', {
        name: file.name,
        size: FILE_UPLOAD_MAX_MEGABYTES,
      });
      continue;
    }
    if (!FILE_UPLOAD_ALLOWED_MIME_TYPES.includes(file.type)) {
      uploadError.value = t('candidate.public_upload.error_type', { name: file.name });
      continue;
    }
    mergedFiles.push(file);
  }

  selectedFiles.value = mergedFiles;
}

function onFileChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) {
    addFiles(Array.from(input.files));
  }
  // lets the same file be picked again after removing it
  input.value = '';
}

function onDrop(event: DragEvent): void {
  isDragging.value = false;
  if (isUploading.value || !event.dataTransfer?.files.length) return;
  addFiles(Array.from(event.dataTransfer.files));
}

function removeFile(index: number): void {
  selectedFiles.value = selectedFiles.value.filter((_, fileIndex) => fileIndex !== index);
  uploadError.value = null;
}

async function loadFileRequest(): Promise<void> {
  try {
    fileRequest.value = await fetchPublicFileRequest(token);
    postingTranslator.value = createPostingTranslator(fileRequest.value.language);
  } catch (error) {
    if (isOrderFinishedError(error)) {
      loadError.value = t('candidate.public_upload.order_finished');
    } else if (error instanceof ClientError && error.response.status === 404) {
      loadError.value = t('candidate.public_upload.error_invalid');
    } else if (error instanceof ClientError) {
      loadError.value = t('candidate.public.error_generic');
    } else {
      loadError.value = t('common.network_error');
    }
  } finally {
    isLoading.value = false;
  }
}

async function submit(): Promise<void> {
  if (!selectedFiles.value.length || isUploading.value) return;

  isUploading.value = true;
  uploadError.value = null;

  try {
    await uploadFileRequestFiles(token, selectedFiles.value);
    isUploaded.value = true;
  } catch (error) {
    if (!(error instanceof ClientError)) {
      uploadError.value = t('common.network_error');
      return;
    }

    if (isOrderFinishedError(error)) {
      loadError.value = t('candidate.public_upload.order_finished');
      return;
    }

    uploadError.value = t('candidate.public_upload.upload_failed');
    // the request may have expired, been withdrawn or fulfilled meanwhile; reloading shows that state
    if (error.response.status === 400) {
      await loadFileRequest();
    }
  } finally {
    isUploading.value = false;
  }
}

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(loadFileRequest);
</script>
